import React, { useState } from 'react';

import { SkaterWaiver } from '@types';
import { ProgressIndicator } from '_atoms';
import WaiverNew from '_molecules/WaiverNew';
import WaiverService from '_services/WaiverService';

export type WaiverStep = 'waiver' | 'confirmation';

export interface WaiverData {
  waiverAccepted?: boolean;
  skaterWaivers?: SkaterWaiver[];
  signedPDF?: File;
  // Additional fields
  waiverId?: string;
  barcodeId?: string;
  fileName?: string;
  isExistingWaiver?: boolean;
  adult?: any;
  minors?: any[];
}

const STEPS = [
  { id: 'waiver', label: 'Waiver' },
  { id: 'confirmation', label: 'Confirmation' },
];

const SignWaiverFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<WaiverStep>('waiver');
  const [waiverData, setWaiverData] = useState<WaiverData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [, setIsSubmitted] = useState(false);

  const handleNext = async (stepData?: Partial<WaiverData>) => {
    console.log('Step data:', stepData);
    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    console.log('Current index:', currentIndex);
    console.log('Current step:', currentStep);
    console.log('Next step:', STEPS[currentIndex + 1].id);

    if (currentIndex < STEPS.length - 1) {
      // If we're moving from waiver step to success step, submit the data first
      if (
        currentStep === 'waiver' &&
        STEPS[currentIndex + 1].id === 'confirmation'
      ) {
        // Update state and submit with the new data
        const updatedWaiverData = { ...waiverData, ...stepData };
        await submitWaiverDataWithData(updatedWaiverData);

        console.log('Updated waiver data:', updatedWaiverData);
      } else {
        // Update state for other steps
        if (stepData) {
          setWaiverData(prev => ({ ...prev, ...stepData }));
        }
        setCurrentStep(STEPS[currentIndex + 1].id as WaiverStep);

        console.log('Current step:', currentStep);
        console.log('Next step:', STEPS[currentIndex + 1].id);
      }
    } else {
      // This is the final step, submit the waiver data
      const updatedWaiverData = { ...waiverData, ...stepData };
      await submitWaiverDataWithData(updatedWaiverData);
    }
  };

  const handlePrevious = () => {
    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id as WaiverStep);
    }
  };

  const downloadSignedPDF = () => {
    if (waiverData.signedPDF) {
      const url = URL.createObjectURL(waiverData.signedPDF);
      const link = document.createElement('a');
      link.href = url;
      link.download = waiverData.fileName || 'waiver.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const submitWaiverDataWithData = async (data: WaiverData) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Create FormData to send to the API
      const formData = new FormData();

      // Option 1: Send as JSON string (current approach)
      formData.append(
        'waiverData',
        JSON.stringify({
          waiverAccepted: data.waiverAccepted,
          skaterWaivers: data.skaterWaivers,
          submittedAt: new Date().toISOString(),
          // Include adult object with all fields
          adult: data.adult,
          minors: data.minors,
          waiverId: data.waiverId,
          barcodeId: data.barcodeId,
          fileName: data.fileName,
          isExistingWaiver: data.isExistingWaiver,
        }),
      );

      formData.append(
        'signedPDFs',
        data.signedPDF || new Blob(),
        `${data.skaterWaivers?.[0]?.skaterName}_waiver.pdf`,
      );

      // APPROACH 1: File Objects (Recommended for most cases)
      // Pros:
      // - Smaller payload size (files are compressed)
      // - Better for large files
      // - Standard way to handle file uploads
      // - Browser handles file encoding automatically
      // Cons:
      // - Requires multipart/form-data
      // - Server needs to handle file uploads
      for (const waiver of data.skaterWaivers || []) {
        if (waiver.signedPDF) {
          formData.append(
            'signedPDFs',
            waiver.signedPDF,
            `${waiver.skaterName}_waiver.pdf`,
          );
        }
      }

      // Submit to API using WaiverService
      const response = await WaiverService.createUserWaiver(formData);

      console.log('Waiver submitted successfully:', response);

      // Update state with the submitted data
      setWaiverData(data);

      // Set submitted state and navigate to success step
      setIsSubmitted(true);
      setCurrentStep('confirmation');
    } catch (error: any) {
      console.error('Error submitting waiver:', error);
      setSubmitError(
        error?.detail ||
          error?.message ||
          'Failed to submit waiver. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'waiver':
        return (
          <WaiverNew
            onPrevious={handlePrevious}
            onContinue={handleNext}
            isInFlow={false}
          />
        );
      case 'confirmation':
        return (
          <div className="text-center py-8">
            {/* Success Icon */}
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            {/* Success Message */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Waiver Submitted Successfully!
            </h2>
            <p className="text-gray-600 mb-4 max-w-md mx-auto">
              Thank you for completing the waiver process. Your signed waivers
              have been submitted and are now on file.
            </p>
            {waiverData.barcodeId && (
              <p className="text-sm text-gray-700 mb-8">
                Waiver Number:{' '}
                <span className="font-mono font-semibold text-blue-700 tracking-wide">
                  {waiverData.barcodeId}
                </span>
              </p>
            )}

            {/* Download Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Download Your Waiver
              </h3>

              {/* Download Signed PDF Button */}
              {waiverData.signedPDF && (
                <div className="mb-6">
                  <button
                    onClick={downloadSignedPDF}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span>Download Signed Waiver PDF</span>
                  </button>
                </div>
              )}
            </div>

            {/* Additional Actions */}
            <button
              onClick={() => (window.location.href = '/')}
              className="w-full px-4 py-3 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Return to Home
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-0 md:p-6">
      {/* Progress Indicator */}
      <ProgressIndicator steps={STEPS} currentStep={currentStep} />

      {/* Current Step Content */}
      <div className="bg-white rounded-lg shadow-lg p-2 md:p-6">
        {renderCurrentStep()}
      </div>

      {/* Debug: Show waiver data */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Debug Data:
          </h3>
          <pre className="text-xs text-gray-600 overflow-auto">
            {JSON.stringify(waiverData, null, 2)}
          </pre>
        </div>
      )}

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex flex-col items-center">
              {/* Spinner */}
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>

              {/* Loading Text */}
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Submitting Your Waiver
              </h3>
              <p className="text-gray-600 text-center">
                Please wait while we process your waiver submission...
              </p>

              {/* Error Message */}
              {submitError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg w-full">
                  <p className="text-sm text-gray-600 max-w-md text-red-700 whitespace-pre-wrap">
                    {submitError}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignWaiverFlow;
